import { isAxiosError, isCancel } from "axios";
import type { Finding, FindingDecision, GenerateResult, PlaybookChange, RunSummary, Severity } from "../../services/lensService";

const KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const isValidKey = (key: string) => key.length <= 60 && KEY_PATTERN.test(key);

// "Settlement Agreement (E&W)" -> "settlement-agreement-e-w"
export const toKey = (name: string) =>
    name
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60)
        .replace(/-+$/, "");

export const formatWhen = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime())
        ? ""
        : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const formatElapsed = (ms: number): string => {
    const seconds = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

export const formatDuration = (ms: number | null | undefined): string => {
    if (!ms) return "—";
    const seconds = Math.round(ms / 1000);
    return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`;
};

export const runBy = (run: Pick<RunSummary, "requestedByName" | "requestedByEmail">): string =>
    run.requestedByName || run.requestedByEmail || "—";

// Older runs have no email, so they are never anyone's.
export const isRunBy = (run: Pick<RunSummary, "requestedByEmail">, email: string | null | undefined): boolean =>
    !!email && !!run.requestedByEmail && run.requestedByEmail.toLowerCase() === email.toLowerCase();

// No response at all, or a bare 502/503/504: the gateway dropped the request but the server carries on.
export const isGatewayCutOff = (error: unknown): boolean => {
    if (!isAxiosError(error) || isCancel(error)) return false;
    if (!error.response) return true;
    const data = error.response.data as { error?: string; title?: string } | undefined;
    const hasApiBody = !!data && typeof data === "object" && !!(data.error || data.title);
    return [502, 503, 504].includes(error.response.status) && !hasApiBody;
};

export const apiErrorMessage = (error: unknown, fallback: string): string => {
    const data = (isAxiosError(error) ? error.response?.data : undefined) as { error?: string; title?: string } | undefined;
    return data?.error ?? data?.title ?? fallback;
};

export const SEVERITIES: Severity[] = ["critical", "material", "preference"];

export const severityColour: Record<Severity, "error" | "warning" | "info"> = {
    critical: "error",
    material: "warning",
    preference: "info",
};

export type DecisionFilter = FindingDecision | "notassessed";

export const isNotAssessed = (finding: Finding) => finding.type === "uncoveredsection";

// A not-assessed section has no severity verdict, so only the "not assessed" decision filter reaches it.
export const matchesFilters = (finding: Finding, severity: Severity | null, decision: DecisionFilter | null) =>
    (!severity || (!isNotAssessed(finding) && finding.severity === severity)) &&
    (!decision || (decision === "notassessed" ? isNotAssessed(finding) : !isNotAssessed(finding) && finding.decision === decision));

export const readFileBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
        reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}`));
        reader.readAsDataURL(file);
    });

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

// e.g. "Drafted 120 versions of 74 provisions from 2 documents · 18 merged · 9 need review".
export const generateResultMessage = (result: GenerateResult, source: "generated" | "drafted"): string => {
    if (source === "drafted" && result.added === 0) return "No new provisions found in that text — nothing was added";

    const drafted =
        result.draftedCount > result.added
            ? `${result.draftedCount} versions of ${plural(result.added, "provision")}`
            : plural(result.added, "provision");
    const from = result.documentsRead > 0 ? ` from ${plural(result.documentsRead, "document")}` : "";
    const parts = [`Drafted ${drafted}${from}`];
    if (result.mergedCount > 0) parts.push(`${result.mergedCount} merged`);
    if (result.flaggedCount > 0) parts.push(`${result.flaggedCount} need${result.flaggedCount === 1 ? "s" : ""} review`);
    return parts.join(" · ");
};

// The latest AI draft's result, as the changelog (newest first) recorded it. A first draft of a new playbook has no
// version in its detail, since the playbook starts at its initial one.
export const latestDraftResult = (
    key: string,
    changes: PlaybookChange[],
): { result: GenerateResult; source: "generated" | "drafted" } | null => {
    const change = changes.find((c) => c.action === "ai-draft-from-precedent");
    if (!change) return null;
    try {
        const detail = JSON.parse(change.detail ?? "{}");
        const added: string[] = Array.isArray(detail.added) ? detail.added : [];
        return {
            result: {
                key,
                added: added.length,
                provisionKeys: added,
                model: detail.model ?? null,
                draftedCount: detail.drafted ?? added.length,
                mergedCount: detail.merged ?? 0,
                flaggedCount: detail.flagged ?? 0,
                documentsRead: detail.documentsRead ?? 0,
                precedentCharacters: 0,
            },
            source: detail.version ? "drafted" : "generated",
        };
    } catch {
        return null;
    }
};
